import React, { useState, useEffect } from 'react';
import { CustomTable, Loader, Search, SuccessModal, ConfirmationPopup, Pagination } from '@ohif/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import UrgencyForm from './UrgencyForm';
import getUrgencyApi from '../rtk-apis/billing/urgency/getUrgency';
import addUrgency from '../rtk-apis/billing/urgency/addUrgency';
import deleteUrgencyApi from '@custom/rtk-apis/billing/urgency/deleteUrgency';
import editUrgencyApi from '../rtk-apis/billing/urgency/editUrgency';
import { servicesManager } from '../../app/src/App';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { getCurrencySymbol } from '@custom/Utils/utils';
import { useNavigate } from 'react-router-dom';

interface ConsultationProps {
  activeTab: string;
  setActiveTab?: (tab: string) => void;
}

const Urgency = (props: ConsultationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const getUrgencyData = useSelector((state: RootState) => state?.getUrgencyResponse);
  const addUrgencyData = useSelector((state: RootState) => state?.addUrgencyResponse);
  const deleteUrgencyData = useSelector((state: RootState) => state?.deleteUrgencyResponse);
  const editUrgencyData = useSelector((state: RootState) => state?.editUrgencyResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  const { uiNotificationService } = servicesManager.services;
  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUrgencyForm, setShowUrgencyForm] = useState(false);
  const [editUrgency, setEditUrgency] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [currency, setCurrency] = useState("");

  useEffect(() => {
    dispatch(getBillingGeneralSettings());
  }, [dispatch])

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  const columns = [
    { key: 'case', title: 'Case' },
    { key: 'price', title: 'Price', sort: true },
    { key: 'actions', title: 'Action' },
  ];

  const defaultInitialValues = {
    urgencyCase: '',
    urgencyPrice: '',
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  useEffect(() => {
    if (props.activeTab === 'urgency') {
      dispatch(getUrgencyApi({ search: searchQuery, page: pgNumber, limit: pgLimit }));
    }
  }, [dispatch, props.activeTab, searchQuery, pgNumber, pgLimit]);

  useEffect(() => {
    if (getUrgencyData?.result) {
      const formattedData = getUrgencyData.result.map(item => ({
        ...item,
        price: `${getCurrencySymbol(currency)}${item.price || 0}`,
      }));
      setTableData(formattedData);
    }
  }, [getUrgencyData, currency]);  

  useEffect(() => {
    if (addUrgencyData?.status === 'success') {
      setShowUrgencyForm(false);
      dispatch(getUrgencyApi({ search: searchQuery, page: pgNumber, limit: pgLimit }));
    } else if (addUrgencyData?.status === 'error') {
      uiNotificationService.show({
        title: 'Case Error',
        message: addUrgencyData?.msg || 'An error occurred while adding the urgency case.',
        type: 'error',
      });
      setShowUrgencyForm(false);
    }
  }, [addUrgencyData, dispatch, uiNotificationService]);

  useEffect(() => {
    if (editUrgencyData?.status === 'success') {
      setMsg('Urgency charges updated successfully.');
      setSuccess(true);
      setShowUrgencyForm(false);
      dispatch(getUrgencyApi({ search: searchQuery, page: pgNumber, limit: pgLimit }));
    } else if (editUrgencyData?.status === 'error') {
      uiNotificationService.show({
        title: 'Edit Error',
        message: editUrgencyData?.msg || 'An error occurred while updating the urgency case.',
        type: 'error',
      });
      setShowUrgencyForm(false);
    }
  }, [editUrgencyData, dispatch, uiNotificationService]);

  useEffect(() => {
    if (deleteUrgencyData?.status === 'success') {
      dispatch(getUrgencyApi({ search: searchQuery, page: pgNumber, limit: pgLimit }));
    } else if (deleteUrgencyData?.status === 'error') {
      uiNotificationService.show({
        title: 'Delete Error',
        message: deleteUrgencyData?.msg || 'An error occurred while deleting the urgency case.',
        type: 'error',
      });
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  }, [deleteUrgencyData, dispatch, uiNotificationService]);

  const submitForm = async (values) => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
    const payload = {
      hospitalId,
      case: values.urgencyCase,
      price: parseInt(values.urgencyPrice, 10),
    };

    if (edit && editUrgency) {
      payload["_id"] = editUrgency._id;
      await dispatch(editUrgencyApi(payload));
    } else {
      await dispatch(addUrgency(payload));
    }
  };

  const handleAddCharges = () => {
    if (billingGeneralSettingsData?.msg === "General settings not found for this hospital.") {
      props.setActiveTab?.('general');
      uiNotificationService.show({
        title: 'Billing Settings',
        message: 'Fill the general settings to create appointment.',
        type: 'error',
      });
      navigate('/billing');
    } else {
      setEditUrgency(null);
      setEdit(false);
      setInitialValues(defaultInitialValues);
      setShowUrgencyForm(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      const payload = { _id: deleteId };
      await dispatch(deleteUrgencyApi(payload));
    }
  };

  const handleDelete = (item) => {
    setDeleteId(item._id);
    setShowDeleteModal(true);
  };

  const handleCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const actions = [
    {
      id: 'editUrgency',
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        setEditUrgency(item);
        setEdit(true);
        setInitialValues({
          urgencyCase: item.case,
          urgencyPrice: item.price.replace(/[^0-9.]/g, ''),
        });
        setShowUrgencyForm(true);
      },
    },
    {
      id: 'deleteUrgency',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => handleDelete(item),
    },
  ];

  const searchClick = (searchText) => {
    setSearchInputValue(searchText);
    setSearchQuery(searchText);
    setPgNumber(1);
    dispatch(getUrgencyApi({ search: searchText, page: 1, limit: pgLimit }));
  };

  return (
    <>
      <div className="page__body">
        <div className="tableWrap">
          {!showUrgencyForm ? (
            <>
              <div className="tableWrap__head">
                <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                  <div className="formCol">
                    <Search
                      text="Search"
                      onSearchClick={searchClick}
                      value={searchInputValue}
                      setValue={setSearchInputValue}
                    />
                  </div>
                </div>
                {props.activeTab === 'urgency' && (
                  <div className="btn__group">
                    <button className="btn btn__white" onClick={handleAddCharges}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684"
                          stroke="#150823"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add Urgency Charges
                    </button>
                  </div>
                )}
              </div>
              <div className="tableContainer">
                <CustomTable
                  columns={columns}
                  data={tableData}
                  actions={actions}
                  searchQuery={searchQuery}
                  from='urgencyCharges'
                />
              </div>
            </>
          ) : (
            <UrgencyForm
              editUrgency={editUrgency}
              initialValues={initialValues}
              submitForm={submitForm}
              edit={edit}
              setEdit={setEdit}
              setShowUrgencyForm={setShowUrgencyForm}
              isSubmitting={addUrgencyData?.loading || editUrgencyData?.loading}
            />
          )}
        </div>
      </div>
      <SuccessModal
        isSuccessModalOpen={success}
        title={msg}
        handleCloseModal={() => setSuccess(false)}
      />
      <Loader isLoaderOpen={getUrgencyData?.loading || addUrgencyData?.loading || deleteUrgencyData?.loading || editUrgencyData?.loading} />
      <ConfirmationPopup
        isOpen={showDeleteModal}
        title=""
        message="Are you sure you want to delete this Case?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleCancel}
      />
      {tableData.length > 0 && !showUrgencyForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={tableData.length} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default Urgency;
